import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiCheck, FiChevronLeft, FiChevronRight, FiCopy, FiBarChart2, FiDownload, FiEdit2, FiEdit3, FiPlay, FiPlus, FiRefreshCw, FiRotateCcw, FiSliders, FiTrash2, FiUpload, FiX } from "react-icons/fi";
import { useDebounce } from "use-debounce";
import Header from "../Components/Header";
import ModelPerformance, { accuracyValue } from "../Components/ModelPerformance";
import "../assets/css/RiskPrediction.css";
import "../assets/css/ModelTraining.css";
import {
  getGeneratedRecords,
  createGeneratedRecords,
  addRecordBySrNo,
  addManualRecord,
  updateRecordLocation,
  duplicateGeneratedRecord,
  processGeneratedRecord,
  deleteGeneratedRecord,
  processAllGeneratedRecords,
  uploadRecordsCsv,
  downloadSampleCsv,
  resetAllGeneratedRecords,
  deleteAllGeneratedRecords,
  getPredictionFeedback,
  savePredictionFeedback,
  deletePredictionFeedback,
  getFeedbackModelStatus,
} from "../Services/apiService";

// model inputs, in the same order as schisto_risk_model_baseline.py FEATURES.
// `scale` shows a 0-1 fraction as a percentage in the form.
const FIELDS = [
  { key: "elevation_m", label: "Elevation", unit: "m" },
  { key: "temperature_mean_c", label: "Temperature", unit: "°C" },
  { key: "temperature_mean_3m", label: "Temp. 3 months", unit: "°C" },
  { key: "humidity_pct", label: "Humidity", unit: "%" },
  { key: "rainfall_mm", label: "Rain 12 months", unit: "mm" },
  { key: "rainfall_cumulative_3m", label: "Rain 3 months", unit: "mm" },
  { key: "water_distance_m", label: "Nearest water", unit: "m" },
  { key: "proximity_to_irrigation_km", label: "Irrigation", unit: "km" },
  { key: "ndvi_index", label: "NDVI", unit: "" },
  { key: "ndwi_index", label: "NDWI", unit: "" },
  { key: "soil_type", label: "Soil type", options: ["clay", "muddy", "loam", "sandy", "rocky"] },
  { key: "snail_habitat_presence", label: "Snail habitat", options: ["true", "false"] },
  { key: "population_density", label: "Population", unit: "/km²" },
  { key: "school_age_ratio", label: "School-age", unit: "%", scale: 100 },
  { key: "dhs_25km_open_defecation", label: "Open defecation", unit: "%", scale: 100 },
  { key: "dhs_25km_sanitation_improved", label: "Sanitation", unit: "%", scale: 100 },
  { key: "dhs_25km_water_improved", label: "Improved water", unit: "%", scale: 100 },
  { key: "mda_rounds_effective", label: "MDA rounds", unit: "" },
  { key: "mda_years_since_start", label: "MDA years", unit: "" },
];

const BAND_STYLES = {
  High: { background: "#fde2e2", color: "#9b1c1c", borderColor: "#f8b4b4" },
  Moderate: { background: "#fef3c7", color: "#92400e", borderColor: "#fcd98a" },
  Low: { background: "#e0f2fe", color: "#075985", borderColor: "#a5d8f5" },
};
// level of the % as shown (1 decimal), so 9.96 -> "10.0%" is Moderate, not Low
const band = (pct) => {
  const v = Number(Number(pct).toFixed(1));
  return v >= 50 ? "High" : v >= 10 ? "Moderate" : "Low";
};
// prediction vs actual, by the gap in percentage points (same rule as the
// backend): <8 -> 100%, 8-15 -> 80%, 15-25 -> 60%, 25-35 -> 30%, more -> 0%
const matchScore = (predicted, actual) => {
  const gap = Math.abs(Number(predicted) - Number(actual));
  return gap < 8 ? 100 : gap <= 15 ? 80 : gap <= 25 ? 60 : gap <= 35 ? 30 : 0;
};
const MATCH_CLASS = { 100: "full", 80: "good", 60: "fair", 30: "low", 0: "none" };
function MatchBadge({ value }) {
  if (value === null || value === undefined) return <span className="rp-muted">—</span>;
  return <span className={`mt-match mt-match--${MATCH_CLASS[value]}`}>{value}% accuracy</span>;
}
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);

// stored row value -> form text, and back
function toForm(field, v) {
  if (v === null || v === undefined) return "";
  if (field.options) return String(v);
  const n = Number(v) * (field.scale || 1);
  const d = Math.abs(n) >= 100 ? 10 : 100;
  return String(Math.round(n * d) / d);
}
function fromForm(field, s) {
  if (s === "" || s === null || s === undefined) return null;
  if (field.key === "snail_habitat_presence") return s === "true";
  if (field.options) return s;
  const n = Number(s);
  return Number.isFinite(n) ? n / (field.scale || 1) : null;
}
const inputsOf = (form) =>
  Object.fromEntries(FIELDS.map((f) => [f.key, fromForm(f, form[f.key])]));

// training min / max (from the model status) in the units shown in the form
function rangeOf(field, ranges) {
  const r = ranges?.[field.key];
  if (!r || field.options) return null;
  const k = field.scale || 1;
  const round = (v) => Math.round(v * k * 100) / 100;
  return { min: round(r.min), max: round(r.max) };
}
// compact number for the range header: 3199.27 -> 3199, 0.599 -> 0.6
const short = (v) => (Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 100) / 100);
// true when the form value is a number the model never saw in training
function isOutOfRange(field, text, ranges) {
  const r = rangeOf(field, ranges);
  if (!r || text === "" || text === null || text === undefined) return false;
  const n = Number(text);
  return Number.isFinite(n) && (n < r.min || n > r.max);
}

function RiskBadge({ pct }) {
  if (pct === null || pct === undefined) return <span className="rp-badge">—</span>;
  const b = band(pct);
  return <span className="rp-badge" style={BAND_STYLES[b]}>{b}</span>;
}

// what an upload left out: skipped rows (values outside the range) and
// columns the file did not have, in the units shown on the page
function uploadDetails(r) {
  const fieldOf = (k) => FIELDS.find((f) => f.key === k) || { label: k };
  const shown = (f, v) => short(Number(v) * (f.scale || 1));
  const skipped = (r.skipped || []).map((row) => {
    const parts = row.outside.map((o) => {
      const f = fieldOf(o.field);
      return `${f.label} ${shown(f, o.value)} (range ${shown(f, o.min)} – ${shown(f, o.max)})`;
    });
    return `Row ${row.row}${row.name ? ` (${row.name})` : ""}: ${parts.join(", ")}`;
  });
  const missing = (r.missing_columns || []).map((k) => `${fieldOf(k).label} (${k})`);
  return { skipped, missing };
}

// page buttons: first, last, and two either side of the current one
function pageNumbers(page, pages) {
  const out = [];
  for (let n = 1; n <= pages; n++) {
    if (n === 1 || n === pages || Math.abs(n - page) <= 2) out.push(n);
    else if (out[out.length - 1] !== "…") out.push("…");
  }
  return out;
}

// held-out performance on the surveyed locations, re-measured in the
// background after every retrain (never blocks Verify / Wrong prediction);
// "How measured" opens what each number means
function AccuracyLine({ status }) {
  const acc = status?.accuracy;
  // all Leave-One-Woreda-Out; accuracy = 100 - MAE
  const lowo = acc?.lowo;
  if (!acc && !status?.measuringAccuracy) return null;
  return (
    <div
      className="mt-accuracy"
      title={lowo ? `LOWO = Leave-One-Woreda-Out (${lowo.woredas} woredas); ${lowo.locations} sites · measured ${new Date(acc.measured_at).toLocaleString()}` : undefined}
    >
      {lowo ? (
        <>
          <span className="mt-accuracy__label">Mean absolute error</span>
          <span className="mt-accuracy__value">{lowo.mae_pct_points.toFixed(1)} pts</span>
          <span className="mt-accuracy__dot">·</span>
          <span className="mt-accuracy__label">RMSE</span>
          <span className="mt-accuracy__value">{lowo.rmse_pct_points.toFixed(1)} pts</span>
          <span className="mt-accuracy__dot">·</span>
          <span className="mt-accuracy__label">Same risk level</span>
          <span className="mt-accuracy__value">{lowo.same_level_pct.toFixed(1)}%</span>
          <span className="mt-accuracy__when">(LOWO)</span>
          <span className="mt-accuracy__dot">·</span>
          <span className="mt-accuracy__label">Accuracy</span>
          <span className="mt-accuracy__value">{accuracyValue(acc).toFixed(1)}%</span>
          <span className="mt-accuracy__when">(by MAE)</span>
        </>
      ) : (
        acc && <span className="mt-accuracy__when">Leave-One-Woreda-Out not measured yet</span>
      )}
      {status?.measuringAccuracy && (
        <span className="mt-accuracy__when">
          <FiRefreshCw className="mt-spin" /> Measuring…
        </span>
      )}
      {lowo && (
        <details className="mt-accuracy__how">
          <summary>How measured</summary>
          <div className="mt-accuracy__pop">
            <ModelPerformance perf={acc} />
          </div>
        </details>
      )}
    </div>
  );
}

// first line of a CSV -> column names (handles quoted names)
function csvHeader(text) {
  const line = text.replace(/^\uFEFF/, "").split(/\r?\n/)[0] || "";
  const out = [];
  let cur = "";
  let q = false;
  for (const ch of line) {
    if (ch === '"') q = !q;
    else if (ch === "," && !q) {
      out.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  out.push(cur.trim());
  return out.filter(Boolean);
}

// wait until at least `ms` have passed since `t0`
// one CSV row per record: name, location, result, verification, model inputs
const CSV_HEAD = [
  "name", "batch", "status", "location", "woreda", "survey_date", "real_prevalence_pct",
  "predicted_prevalence_pct", "predicted_risk", "model", "verdict", "actual_prevalence_pct", "match_pct",
  ...FIELDS.map((f) => f.key),
];
const csvCell = (v) => {
  const t = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
};
function recordsCsv(records) {
  const rows = records.map((r) => {
    const v = r.verification;
    const inputs = r.prediction?.inputs || r.inputs || {};
    return [
      r.name, r.batch, statusOf(r), r.location?.name, r.location?.woreda, r.location?.survey_date,
      r.real_prevalence_pct, r.prediction?.prevalence_pct, r.prediction?.risk, r.prediction?.model,
      v?.is_correct == null ? "" : v.is_correct ? "Correct" : "Wrong", v?.actual_prevalence_pct, v?.match_pct,
      ...FIELDS.map((f) => inputs[f.key]),
    ].map(csvCell).join(",");
  });
  return [CSV_HEAD.join(","), ...rows].join("\n");
}

const waitAtLeast = (t0, ms) => new Promise((r) => setTimeout(r, Math.max(0, ms - (Date.now() - t0))));

// Upload CSV popup: choose file -> read & check (short buffer) -> Process
// -> result or errors shown here -> Done closes it
function UploadModal({ onClose, onUploaded }) {
  const input = useRef(null);
  const [stage, setStage] = useState("pick"); // pick | reading | ready | processing | done | error
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { rows, found, missing }
  const [result, setResult] = useState(null); // { ok, text, skipped, missing }
  const [dragging, setDragging] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sampleError, setSampleError] = useState(null);
  const busy = stage === "reading" || stage === "processing";

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const choose = async (f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
    setPreview(null);
    if (!/\.csv$/i.test(f.name)) {
      setResult({ ok: false, text: `${f.name} is not a .csv file.` });
      setStage("error");
      return;
    }
    setStage("reading");
    const t0 = Date.now();
    try {
      const text = await f.text();
      const header = csvHeader(text);
      const rows = text.split(/\r?\n/).slice(1).filter((l) => l.trim()).length;
      const expected = FIELDS.map((x) => x.key);
      const found = expected.filter((k) => header.includes(k));
      const missing = FIELDS.filter((x) => !header.includes(x.key)).map((x) => `${x.label} (${x.key})`);
      // buffer so the check is visible: 5 s + 15 ms per row (at most 12 s)
      await waitAtLeast(t0, Math.min(5000 + rows * 15, 12000));
      setPreview({ rows, found: found.length, expected: expected.length, missing });
      if (!rows) {
        setResult({ ok: false, text: "The file has no data rows." });
        setStage("error");
      } else if (missing.length) {
        // wrong format: stop here, nothing to process
        setResult({
          ok: false,
          text: "This file is not in the right format (same columns as the sample CSV). Fix it and choose it again.",
          missing,
        });
        setStage("error");
      } else {
        setStage("ready");
      }
    } catch (err) {
      setResult({ ok: false, text: `Could not read the file: ${err.message}` });
      setStage("error");
    }
  };

  const process = async () => {
    setStage("processing");
    const t0 = Date.now();
    try {
      const r = await uploadRecordsCsv(file);
      // at least 5 s + 10 ms per row (at most 10 s), longer if the server takes longer
      await waitAtLeast(t0, Math.min(5000 + (preview?.rows || 0) * 10, 10000));
      setResult({
        ok: true,
        text: `Added ${r.added} record${r.added === 1 ? "" : "s"}.`,
        ...uploadDetails(r),
      });
      setStage("done");
      onUploaded();
    } catch (err) {
      setResult({ ok: false, text: err.message, ...(err.details ? uploadDetails(err.details) : {}) });
      setStage("error");
    }
  };

  const getSample = async () => {
    setDownloading(true);
    setSampleError(null);
    try {
      await downloadSampleCsv();
    } catch (err) {
      setSampleError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const reset = () => {
    setStage("pick");
    setFile(null);
    setPreview(null);
    setResult(null);
  };

  return (
    <div className="rp-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="rp-modal mt-upload" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="rp-modal__head">
          <div>
            <h2>Upload Records</h2>
          </div>
          <button className="rp-modal__close" onClick={onClose} disabled={busy} aria-label="Close">
            <FiX />
          </button>
        </div>

        <div className="rp-modal__body mt-upload__body">
          {/* 1. choose a file */}
          {(stage === "pick" || stage === "error") && !file && (
            <div
              className={`mt-drop ${dragging ? "mt-drop--over" : ""}`}
              onClick={() => input.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                choose(e.dataTransfer.files?.[0]);
              }}
            >
              <FiUpload className="mt-drop__icon" />
              <b>Drop a CSV file here, or click to choose</b>
              <span className="rp-muted">.csv · up to 1,000 rows · 5 MB</span>
            </div>
          )}
          <input
            ref={input}
            type="file"
            accept=".csv,text/csv"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              choose(f);
            }}
          />

          {/* chosen file */}
          {file && (
            <div className="mt-file">
              <FiUpload />
              <div className="mt-file__info">
                <b>{file.name}</b>
                <span className="rp-muted">
                  {(file.size / 1024).toFixed(1)} KB
                  {preview &&
                    ` · ${preview.rows} row${preview.rows === 1 ? "" : "s"} · ${preview.found} of ${preview.expected} model columns`}
                </span>
              </div>
              {!busy && stage !== "done" && (
                <button className="mt-btn mt-btn--icon" onClick={reset} title="Choose another file">
                  <FiX />
                </button>
              )}
            </div>
          )}

          {/* 2. short buffer while the file is read and checked */}
          {stage === "reading" && (
            <div className="mt-progress">
              <FiRefreshCw className="mt-spin" /> Reading and checking the file…
              <div className="mt-progress__bar" />
            </div>
          )}

          {/* 3. ready: summary + Process */}
          {stage === "ready" && preview && (
            <div className="mt-upload__ready">
              <p>
                <FiCheck className="mt-ok" /> File checked — {preview.rows} row
                {preview.rows === 1 ? "" : "s"} ready to process.
              </p>
            </div>
          )}

          {/* 4. processing with a timer */}
          {stage === "processing" && (
            <div className="mt-progress">
              <FiRefreshCw className="mt-spin" /> Predicting {preview?.rows} row{preview?.rows === 1 ? "" : "s"}…
              <div className="mt-progress__bar" />
            </div>
          )}

          {/* 5. result or errors, shown here */}
          {result && (stage === "done" || stage === "error") && (
            <div className={`mt-notice ${result.ok ? "" : "mt-notice--bad"}`}>
              <div className="mt-notice__head">
                {result.ok ? <FiCheck /> : <FiAlertTriangle />} {result.text}
              </div>
              {result.skipped?.length > 0 && (
                <div className="mt-notice__list">
                  <b>
                    Not added — {result.skipped.length} row{result.skipped.length > 1 ? "s" : ""}:
                  </b>
                  <ul>
                    {result.skipped.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.missing?.length > 0 && (
                <div className="mt-notice__list">
                  <b>Missing columns:</b> {result.missing.join(", ")}
                </div>
              )}
            </div>
          )}

          {/* sample file */}
          {stage !== "processing" && stage !== "done" && (
            <div className="mt-upload__sample">
              <span className="rp-muted">Not sure of the format?</span>
              <button className="mt-btn mt-btn--ranges" onClick={getSample} disabled={downloading}>
                <FiDownload /> {downloading ? "Preparing…" : "Download sample CSV (20 records)"}
              </button>
              {sampleError && <span className="rp-error">{sampleError}</span>}
            </div>
          )}
        </div>

        {/* footer actions */}
        <div className="mt-upload__foot">
          {stage === "done" ? (
            <button className="rp-download" onClick={onClose}>
              <FiCheck /> Done
            </button>
          ) : (
            <>
              <button className="mt-btn mt-btn--ranges" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              {stage === "error" && file ? (
                <button className="rp-download" onClick={reset}>
                  Choose another file
                </button>
              ) : (
                <button className="rp-download" onClick={process} disabled={stage !== "ready"}>
                  <FiPlay /> {stage === "processing" ? "Processing…" : "Process"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// popup: every model input with the range the model was trained on
function RangesModal({ ranges, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="rp-modal-backdrop" onClick={onClose}>
      <div className="rp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="rp-modal__head">
          <div>
            <h2>Ranges</h2>
            <p className="rp-muted">
              Values the model was trained on. Generated records always stay inside these.
            </p>
          </div>
          <button className="rp-modal__close" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>
        <div className="rp-modal__body">
          <table className="rp-modal__table">
            <thead>
              <tr>
                <th>Input</th>
                <th>Full range</th>
              </tr>
            </thead>
            <tbody>
              {FIELDS.map((f) => {
                const r = rangeOf(f, ranges);
                return (
                  <tr key={f.key}>
                    <td>
                      {f.label}
                      {f.unit && <span className="mt-unit"> ({f.unit})</span>}
                    </td>
                    <td>{f.options ? f.options.join(" / ") : r ? `${short(r.min)} – ${short(r.max)}` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Add record popup: type every input by hand (plus an optional name,
// location and measured prevalence) -> predicted and added as a record
const EMPTY_FORM = Object.fromEntries(FIELDS.map((f) => [f.key, ""]));
function AddRecordModal({ ranges, onClose, onAdded }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [meta, setMeta] = useState({ name: "", woreda: "", lat: "", lon: "", real: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const out = FIELDS.filter((f) => isOutOfRange(f, form[f.key], ranges));
  const blank = FIELDS.filter((f) => form[f.key] === "");
  const realBad = meta.real !== "" && !(Number(meta.real) >= 0 && Number(meta.real) <= 100);
  const setM = (k) => (e) => setMeta({ ...meta, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await addManualRecord({
        inputs: inputsOf(form),
        name: meta.name,
        woreda: meta.woreda,
        lat: meta.lat,
        lon: meta.lon,
        real_prevalence_pct: meta.real,
      });
      onAdded();
      onClose();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="rp-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="rp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="rp-modal__head">
          <div>
            <h2>Add record</h2>
            <p className="rp-muted">Type each value. A blank input uses the training average.</p>
          </div>
          <button className="rp-modal__close" onClick={onClose} disabled={busy} aria-label="Close">
            <FiX />
          </button>
        </div>
        <div className="rp-modal__body">
          <div className="mt-add__section">Location (optional)</div>
          <div className="mt-add__grid">
            <label className="mt-field">
              <span className="mt-field__label">Name</span>
              <input type="text" value={meta.name} onChange={setM("name")} placeholder="e.g. Jinka Town" />
            </label>
            <label className="mt-field">
              <span className="mt-field__label">Woreda</span>
              <input type="text" value={meta.woreda} onChange={setM("woreda")} />
            </label>
            <label className="mt-field">
              <span className="mt-field__label">Latitude</span>
              <input type="number" step="any" value={meta.lat} onChange={setM("lat")} />
            </label>
            <label className="mt-field">
              <span className="mt-field__label">Longitude</span>
              <input type="number" step="any" value={meta.lon} onChange={setM("lon")} />
            </label>
            <label className={`mt-field ${realBad ? "mt-field--out" : ""}`}>
              <span className="mt-field__label">
                Real prevalence <em>%</em>
              </span>
              <input type="number" step="any" min="0" max="100" value={meta.real} onChange={setM("real")} />
            </label>
          </div>

          <div className="mt-add__section">Model inputs</div>
          <div className="mt-add__grid">
            {FIELDS.map((f) => {
              const r = rangeOf(f, ranges);
              const bad = isOutOfRange(f, form[f.key], ranges);
              return (
                <label key={f.key} className={`mt-field ${bad ? "mt-field--out" : ""}`}>
                  <span className="mt-field__label">
                    {f.label}
                    {f.unit && <em> {f.unit}</em>}
                  </span>
                  {f.options ? (
                    <select value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>
                      <option value="">—</option>
                      {f.options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="number"
                      step="any"
                      value={form[f.key]}
                      placeholder={r ? `${short(r.min)} – ${short(r.max)}` : ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    />
                  )}
                </label>
              );
            })}
          </div>

          {out.length > 0 && (
            <p className="mt-out-msg">
              <FiAlertTriangle /> Outside the range: {out.map((f) => f.label).join(", ")} — fix to add
            </p>
          )}
          {error && <p className="rp-error">{error}</p>}
          <div className="mt-add__foot">
            <span className="rp-muted">
              {blank.length ? `${blank.length} blank — will use the training average` : "All inputs filled"}
            </span>
            <button className="mt-btn mt-btn--ranges" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              className="rp-download"
              onClick={save}
              disabled={busy || out.length > 0 || realBad || blank.length === FIELDS.length}
              title={blank.length === FIELDS.length ? "Enter at least one input" : "Predict and add this record"}
            >
              {busy ? <FiRefreshCw className="mt-spin" /> : <FiPlus />} {busy ? "Adding…" : "Add record"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const formOf = (inputs) =>
  Object.fromEntries(FIELDS.map((f) => [f.key, toForm(f, inputs?.[f.key])]));

// unprocessed = as randomly generated; processed = reviewed with Process
// (older records may still say "verified", which counts as processed)
const statusOf = (r) =>
  r.verification?.is_correct != null ? "verified" : r.status === "unprocessed" ? "unprocessed" : "processed";
const STATUS_LABEL = { unprocessed: "Unprocessed", processed: "Processed", verified: "Verified" };

// why the model gave this result: the sentence from explain_prediction.py
// and the inputs that moved it most (SHAP, % points; red raises, blue
// lowers). Shows what the model learned, not proof of cause.
const WHY_TOP = 8;
// in tenths, so the shown numbers add up exactly
const tenths = (x) => Math.round(x * 10);
const signed = (t) => `${t >= 0 ? "+" : "−"}${(Math.abs(t) / 10).toFixed(1)}`;

function WhyRow({ label, t, max, imputed, className = "" }) {
  return (
    <div className={`mt-why__row ${className}`} title={imputed ? "No data — used the average" : undefined}>
      <span className="mt-why__label">
        {label}
        {imputed && <em> (no data)</em>}
      </span>
      <span className="mt-why__track">
        <span className="mt-why__mid" />
        <span
          className={`mt-why__bar ${t >= 0 ? "up" : "down"}`}
          style={{ width: `${(Math.abs(t) / max) * 50}%`, [t >= 0 ? "left" : "right"]: "50%" }}
        />
      </span>
      <span className={`mt-why__num ${t >= 0 ? "up" : "down"}`}>{signed(t)}</span>
    </div>
  );
}

// waterfall: every step starts where the one before ended, from the
// average place (start) to the model result. Values in tenths of a % point.
function Waterfall({ steps }) {
  const ends = steps.flatMap((s) => [s.from, s.to]);
  const lo = Math.min(0, ...ends);
  const hi = Math.max(...ends, lo + 1);
  const pos = (v) => ((v - lo) / (hi - lo)) * 100;
  return (
    <div className="mt-wf">
      {steps.map((s, i) => {
        const a = Math.min(s.from, s.to);
        const b = Math.max(s.from, s.to);
        const change = s.to - s.from;
        const color = s.kind === "step" ? (change >= 0 ? "up" : "down") : s.kind;
        return (
          <div
            key={s.key}
            className={`mt-wf__row mt-wf__row--${s.kind} ${s.other ? "mt-wf__row--other" : ""}`}
            title={s.imputed ? "No data — used the average" : undefined}
          >
            <span className="mt-why__label">
              {s.label}
              {s.imputed && <em> (no data)</em>}
            </span>
            <span className="mt-wf__track">
              {lo < 0 && <span className="mt-wf__zero" style={{ left: `${pos(0)}%` }} />}
              {/* dashed link from the previous step's end */}
              {i > 0 && s.kind === "step" && <span className="mt-wf__link" style={{ left: `${pos(s.from)}%` }} />}
              <span
                className={`mt-wf__bar ${color}`}
                style={{ left: `${pos(a)}%`, width: `max(${pos(b) - pos(a)}%, 2px)` }}
              />
            </span>
            <span className={`mt-why__num ${s.kind === "step" ? color : ""}`}>
              {s.kind === "step" ? signed(change) : `${(s.to / 10).toFixed(1)}%`}
            </span>
            <span className="mt-wf__run">{s.kind === "step" ? `${(s.to / 10).toFixed(1)}%` : ""}</span>
          </div>
        );
      })}
      <div className="mt-wf__axis">
        <span />
        <span className="mt-wf__ticks">
          <span style={{ left: 0 }}>{(lo / 10).toFixed(1)}%</span>
          {lo < 0 && <span style={{ left: `${pos(0)}%` }}>0%</span>}
          <span style={{ right: 0 }}>{(hi / 10).toFixed(1)}%</span>
        </span>
      </div>
    </div>
  );
}

function ReasonModal({ title, reason, drivers, pct, onClose }) {
  const [view, setView] = useState("bars"); // bars | waterfall
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const all = drivers?.drivers || [];
  const list = all.slice(0, WHY_TOP);
  // start + every input = the model's own number; the inputs not listed are
  // one "Other" row, worked out so the shown (rounded) numbers add up
  const baseT = tenths(drivers?.base ?? 0);
  const modelT = tenths((drivers?.base ?? 0) + all.reduce((a, d) => a + d.shap, 0));
  const otherT = modelT - baseT - list.reduce((a, d) => a + tenths(d.shap), 0);
  const restN = all.length - list.length;
  const max = Math.max(...list.map((d) => Math.abs(tenths(d.shap))), Math.abs(otherT), 1);
  const modelPct = modelT / 10;
  const shownT = tenths(pct ?? modelPct);
  // running total, step by step, for the waterfall
  const steps = [{ key: "start", kind: "start", label: "Average place (start)", from: 0, to: baseT }];
  let run = baseT;
  for (const d of list) {
    const t = tenths(d.shap);
    steps.push({ key: d.feature, kind: "step", label: d.label, imputed: d.imputed, from: run, to: run + t });
    run += t;
  }
  if (restN > 0) {
    steps.push({ key: "other", kind: "step", other: true, label: `Other ${restN} inputs`, from: run, to: run + otherT });
  }
  steps.push({ key: "total", kind: "total", label: "= Model result", from: 0, to: modelT });
  if (shownT !== modelT) {
    steps.push({
      key: "rule",
      kind: "rule",
      label: shownT === 0 && modelT > 0 ? "Snail rule → shown" : "Kept inside 0–100%",
      from: 0,
      to: shownT,
    });
  }

  return (
    <div className="rp-modal-backdrop" onClick={onClose}>
      <div className="rp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="rp-modal__head">
          <div>
            <h2>SHAP Report — {title}</h2>
          </div>
          <button className="rp-modal__close" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>
        <div className="rp-modal__body">
          <p className="mt-why__text">{reason}</p>
          {list.length > 0 && (
            <div className="mt-why__chart">
              <div className="mt-why__head">
                <div className="mt-why__note">
                  {view === "waterfall"
                    ? "Starts at the average place, then each input adds (red) or takes away (blue), step by step"
                    : `Average place ${drivers.base?.toFixed(1)}% · each bar = % points this input added (red) or took away (blue)`}
                </div>
                <div className="mt-why__switch" role="tablist">
                  {["bars", "waterfall"].map((v) => (
                    <button key={v} role="tab" className={view === v ? "active" : ""} onClick={() => setView(v)}>
                      {v === "waterfall" ? "Waterfall" : "Bars"}
                    </button>
                  ))}
                </div>
              </div>
              {view === "waterfall" ? (
                <Waterfall steps={steps} />
              ) : (
                <>
                <div className="mt-why__row mt-why__row--sum">
                  <span className="mt-why__label">Average place (start)</span>
                  <span />
                  <span className="mt-why__num">{(baseT / 10).toFixed(1)}%</span>
                </div>
                {list.map((d) => (
                  <WhyRow key={d.feature} label={d.label} t={tenths(d.shap)} max={max} imputed={d.imputed} />
                ))}
                {restN > 0 && (
                  <WhyRow label={`Other ${restN} inputs`} t={otherT} max={max} className="mt-why__row--other" />
                )}
                <div className="mt-why__row mt-why__row--sum mt-why__row--total">
                  <span className="mt-why__label">= Model result</span>
                  <span />
                  <span className="mt-why__num">{modelPct.toFixed(1)}%</span>
                </div>
                {shownT !== modelT && (
                  <div className="mt-why__row mt-why__row--sum">
                    <span className="mt-why__label">
                      {shownT === 0 && modelT > 0 ? "Snail rule: no snail habitat → 0%" : "Kept inside 0–100%"}
                    </span>
                    <span />
                    <span className="mt-why__num">{(shownT / 10).toFixed(1)}%</span>
                  </div>
                )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Edit details popup: a record's name, woreda and coordinates. Sr No,
// batch, model inputs and the result are not changed.
function EditDetailsModal({ recordId, recordName, loc, onClose, onSaved }) {
  const str = (v) => (v === null || v === undefined ? "" : String(v));
  const [form, setForm] = useState({
    name: str(loc?.name || recordName),
    woreda: str(loc?.woreda),
    lat: str(loc?.lat),
    lon: str(loc?.lon),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const latBad = form.lat !== "" && !(Number(form.lat) >= -90 && Number(form.lat) <= 90);
  const lonBad = form.lon !== "" && !(Number(form.lon) >= -180 && Number(form.lon) <= 180);
  const half = (form.lat === "") !== (form.lon === "");
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const rec = await updateRecordLocation(recordId, form);
      onSaved(rec.location);
      onClose();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="rp-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="rp-modal mt-edit" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="rp-modal__head">
          <div>
            <h2>Edit details</h2>
          </div>
          <button className="rp-modal__close" onClick={onClose} disabled={busy} aria-label="Close">
            <FiX />
          </button>
        </div>
        <div className="rp-modal__body">
          <div className="mt-add__grid mt-edit__grid">
            <label className="mt-field mt-edit__wide">
              <span className="mt-field__label">Name</span>
              <input type="text" value={form.name} onChange={set("name")} autoFocus />
            </label>
            <label className="mt-field mt-edit__wide">
              <span className="mt-field__label">Woreda</span>
              <input type="text" value={form.woreda} onChange={set("woreda")} />
            </label>
            <label className={`mt-field ${latBad || (half && form.lat === "") ? "mt-field--out" : ""}`}>
              <span className="mt-field__label">Latitude</span>
              <input type="number" step="any" value={form.lat} onChange={set("lat")} />
            </label>
            <label className={`mt-field ${lonBad || (half && form.lon === "") ? "mt-field--out" : ""}`}>
              <span className="mt-field__label">Longitude</span>
              <input type="number" step="any" value={form.lon} onChange={set("lon")} />
            </label>
          </div>
          {half && <p className="mt-out-msg">Give both latitude and longitude, or leave both blank.</p>}
          {error && <p className="rp-error">{error}</p>}
          <div className="mt-add__foot">
            <button className="mt-btn mt-btn--ranges" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button className="rp-download" onClick={save} disabled={busy || latBad || lonBad || half}>
              {busy ? <FiRefreshCw className="mt-spin" /> : <FiCheck />} {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// one generated record: shows its result; Process saves (edited) values and
// the new result; Verify / Wrong prediction marks the result right or wrong
function RecordRow({ record, ranges, onChanged, onFeedback, onDelete, onDuplicate, onEditState }) {
  const original = useMemo(() => formOf(record.inputs), [record.inputs]);
  const [form, setForm] = useState(() =>
    record.prediction?.inputs ? formOf(record.prediction.inputs) : original
  );
  // latest saved prediction and the exact values it was made for
  const [result, setResult] = useState(() =>
    record.prediction
      ? {
          pct: record.prediction.prevalence_pct,
          model: record.prediction.model,
          reason: record.prediction.reason,
          drivers: record.prediction.drivers,
          form: formOf(record.prediction.inputs),
        }
      : null
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [verdict, setVerdict] = useState(null); // null | "wrong"
  const [showReason, setShowReason] = useState(false);
  // surveyed location (newer records): where it is and its measured prevalence
  const [loc, setLoc] = useState(record.location);
  const [editDetails, setEditDetails] = useState(false);
  const real = record.real_prevalence_pct ?? null;
  const [actual, setActual] = useState(real != null ? String(real) : "");
  const [notes, setNotes] = useState("");
  const [saved, setSaved] = useState(record.verification?.is_correct != null ? record.verification : null);
  const [status, setStatus] = useState(statusOf(record));
  const edited = FIELDS.filter((f) => form[f.key] !== original[f.key]).map((f) => f.key);
  const stale = !!result && FIELDS.some((f) => form[f.key] !== result.form[f.key]);
  // only values the user typed in; generated values are always in range
  // edited values outside the training range: Process stays blocked until fixed
  const outNow = FIELDS.filter((f) => form[f.key] !== original[f.key] && isOutOfRange(f, form[f.key], ranges));
  // tell the page about unsaved edits, so "Process all" can stop on them
  const outLabels = outNow.map((f) => f.label).join(", ");
  const unsavedEdits = status === "unprocessed" && edited.length > 0;
  useEffect(() => {
    onEditState?.(record._id, { name: record.name, out: outLabels, edited: unsavedEdits });
  }, [record._id, record.name, outLabels, unsavedEdits, onEditState]);
  useEffect(() => () => onEditState?.(record._id, null), [record._id, onEditState]);

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const snapshot = { ...form };
      // the form shows rounded values; a field the user did not change sends
      // its exact stored value, so Process with no edits gives the same result
      const exact = inputsOf(snapshot);
      for (const src of [record.inputs, record.prediction?.inputs]) {
        if (!src) continue;
        const shown = formOf(src);
        for (const f of FIELDS) if (snapshot[f.key] === shown[f.key]) exact[f.key] = src[f.key] ?? null;
      }
      const rec = await processGeneratedRecord(record._id, exact);
      setResult({
        pct: rec.prediction.prevalence_pct,
        model: rec.prediction.model,
        reason: rec.prediction.reason,
        drivers: rec.prediction.drivers,
        form: snapshot,
      });
      setStatus(saved ? "verified" : "processed");
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (isCorrect) => {
    setBusy(true);
    setError(null);
    try {
      const doc = await savePredictionFeedback({
        record_id: record._id,
        location_id: record.name,
        woreda: loc?.name || record.name,
        inputs: inputsOf(result.form),
        edited_fields: FIELDS.filter((f) => result.form[f.key] !== original[f.key]).map((f) => f.key),
        original_prevalence_pct: null,
        model_used: result.model,
        predicted_prevalence_pct: result.pct,
        predicted_risk: band(result.pct),
        is_correct: isCorrect,
        actual_prevalence_pct: isCorrect ? null : actual,
        notes,
      });
      setSaved(doc);
      setStatus("verified");
      onChanged();
      onFeedback();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`mt-item ${saved ? "mt-item--done" : ""}`}>
      <div className="mt-item__bar">
        <div className="mt-item__name">
          {/* record name (e.g. 1-A-09-10-26) hidden for now; the location name leads instead
          <b>{record.name}</b> */}
          <span className="mt-item__title">
            <b>{loc?.name || record.name}</b>
            <button className="mt-coords__btn" onClick={() => setEditDetails(true)} title="Edit name, woreda and coordinates">
              <FiEdit2 />
            </button>
          </span>
          <span className="mt-item__loc">
            {loc?.sr_no != null ? `Sr ${loc.sr_no}` : ""}
            {loc?.woreda && loc.woreda !== loc.name ? `${loc?.sr_no != null ? " · " : ""}${loc.woreda}` : ""}
            {loc?.lat != null && loc?.lon != null && (
              <>
                {loc.sr_no != null || (loc.woreda && loc.woreda !== loc.name) ? " · " : ""}
                <a
                  href={`https://www.google.com/maps?q=${loc.lat},${loc.lon}`}
                  target="_blank"
                  rel="noreferrer"
                  title="Open on the map"
                >
                  {loc.lat.toFixed(4)}, {loc.lon.toFixed(4)}
                </a>
              </>
            )}
          </span>
        </div>
        <span className={`mt-status mt-status--${status}`}>{STATUS_LABEL[status]}</span>

        {real != null && (
          <div className="mt-item__pred" title={loc?.survey_date ? `Measured in the survey, ${loc.survey_date}` : "Measured in the survey"}>
            <span className="mt-item__cap">Real</span>
            <b>{fmtPct(real)}</b>
            <RiskBadge pct={real} />
          </div>
        )}
        {result && (
          <div
            className="mt-item__pred"
            title={!edited.length ? "From a model that did not train on this location" : undefined}
          >
            <span className="mt-item__cap">{real != null ? "Predicted" : "Prediction"}</span>
            <b className={stale ? "mt-stale" : ""}>{fmtPct(result.pct)}</b>
            <RiskBadge pct={result.pct} />
          </div>
        )}
        {real != null && result && !stale && <MatchBadge value={matchScore(result.pct, real)} />}
        {saved && (
          <span className={`mt-verified ${saved.is_correct ? "ok" : "bad"}`}>
            {saved.is_correct ? <FiCheck /> : <FiX />}
            {saved.is_correct ? "Verified correct" : `Wrong prediction (actual ${fmtPct(saved.actual_prevalence_pct)})`}
            {!saved.is_correct && (
              <MatchBadge
                value={saved.match_pct ?? matchScore(saved.predicted_prevalence_pct ?? result?.pct, saved.actual_prevalence_pct)}
              />
            )}
          </span>
        )}
        {stale && <span className="mt-hint">Edited — click Process</span>}
        {outNow.length > 0 && (
          <span className="mt-out-msg" title={outNow.map((f) => f.label).join(", ")}>
            <FiAlertTriangle /> {outNow.length} value{outNow.length > 1 ? "s" : ""} outside the range — fix
            to process
          </span>
        )}

        <div className="mt-item__actions">
          <button
            className="mt-btn mt-btn--reason"
            onClick={() => setShowReason(true)}
            disabled={!result?.reason || stale}
            title={stale ? "Click Process first" : !result?.reason ? "No reason for this result" : "Why the model gave this result"}
          >
            <FiBarChart2 /> SHAP Report
          </button>
          {!saved && (
            <>
              <button
                className="mt-btn mt-btn--icon mt-btn--undo"
                onClick={() => setForm(original)}
                disabled={busy || !edited.length}
                title="Undo edits"
              >
                <FiRotateCcw />
              </button>
              <button
                className="rp-download"
                onClick={generate}
                disabled={busy || outNow.length > 0}
                title={
                  outNow.length
                    ? `Outside the range: ${outNow.map((f) => f.label).join(", ")} — fix to process`
                    : "Calculate the result for these values and save as processed (does not train the model)"
                }
              >
                <FiPlay /> Process
              </button>
              <button
                className="mt-btn mt-btn--ok"
                onClick={() => verify(true)}
                disabled={busy || !result || stale}
                title={!result || stale ? "Click Process first" : "The prediction is correct"}
              >
                <FiCheck /> Verify
              </button>
              <button
                className={`mt-btn mt-btn--bad ${verdict === "wrong" ? "active" : ""}`}
                onClick={() => setVerdict(verdict === "wrong" ? null : "wrong")}
                disabled={busy || !result || stale}
                title="The prediction is wrong — enter the real value"
              >
                <FiX /> Wrong prediction
              </button>
            </>
          )}
          <button
            className="mt-btn mt-btn--icon"
            onClick={() => onDuplicate(record)}
            disabled={busy}
            title="Make a copy"
          >
            <FiCopy />
          </button>
          <button
            className="mt-btn mt-btn--icon"
            onClick={() => onDelete(record)}
            disabled={busy}
            title="Delete this record"
          >
            <FiTrash2 />
          </button>
        </div>
      </div>

      {editDetails && (
        <EditDetailsModal
          recordId={record._id}
          recordName={record.name}
          loc={loc}
          onClose={() => setEditDetails(false)}
          onSaved={setLoc}
        />
      )}

      {showReason && (
        <ReasonModal
          title={loc?.name || record.name}
          reason={result.reason}
          drivers={result.drivers}
          pct={result.pct}
          onClose={() => setShowReason(false)}
        />
      )}

      {!saved && verdict === "wrong" && (
        <div className="mt-item__wrong">
          <span>What is the real prevalence?</span>
          <input
            type="number"
            min="0"
            max="100"
            step="any"
            placeholder="Actual %"
            value={actual}
            onChange={(e) => setActual(e.target.value)}
          />
          {actual !== "" && Number.isFinite(Number(actual)) && result && (
            <span className="mt-match-hint">
              gap {Math.abs(result.pct - Number(actual)).toFixed(1)} pts ·{" "}
              <MatchBadge value={matchScore(result.pct, actual)} />
            </span>
          )}
          <input
            type="text"
            placeholder="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <button className="rp-download" onClick={() => verify(false)} disabled={busy || actual === ""}>
            Save
          </button>
        </div>
      )}

      <div className="mt-fields">
        {FIELDS.map((f) => {
          const range = rangeOf(f, ranges);
          const changed = form[f.key] !== original[f.key];
          const out = changed && isOutOfRange(f, form[f.key], ranges);
          return (
            <label
              key={f.key}
              className={`mt-field ${changed ? "mt-field--edited" : ""} ${out ? "mt-field--out" : ""}`}
              title={`${f.label}${f.unit ? ` (${f.unit})` : ""}`}
            >
              <span className="mt-field__label">
                {f.label}
                {f.unit && <em> {f.unit}</em>}
              </span>
              {f.options ? (
                <select
                  value={form[f.key]}
                  disabled={!!saved}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                >
                  <option value="">—</option>
                  {f.options.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="number"
                  step="any"
                  value={form[f.key]}
                  placeholder="missing"
                  disabled={!!saved}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                />
              )}
              {out ? (
                <span className="mt-out-msg">Outside {short(range.min)} – {short(range.max)}</span>
              ) : (
                <span className="mt-field__range">
                  {range ? `${short(range.min)} – ${short(range.max)}` : "\u00a0"}
                </span>
              )}
            </label>
          );
        })}
      </div>
      {error && <p className="rp-error">{error}</p>}
    </div>
  );
}

export default function ModelTraining() {
  const [records, setRecords] = useState([]);
  const [creating, setCreating] = useState(0); // how many are being made, 0 = idle
  const [newCount, setNewCount] = useState(1); // 1-10 records per batch
  const [showRanges, setShowRanges] = useState(false);
  const validCount = Number.isInteger(Number(newCount)) && newCount >= 1 && newCount <= 10;
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [feedback, setFeedback] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([getFeedbackModelStatus(), getPredictionFeedback()]);
      setStatus(s);
      setFeedback(f);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const [view, setView] = useState("unprocessed"); // unprocessed | processed | verified | all
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const [date, setDate] = useState(""); // YYYY-MM-DD
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [perPage, setPerPage] = useState(5); // records per page: 5, 10, ... 50
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ unprocessed: 0, processed: 0, verified: 0, all: 0 });
  const [bulkBusy, setBulkBusy] = useState(false);
  // unsaved edit state per record on screen (from RecordRow), for Process all
  const editStates = useRef({});
  const onEditState = useCallback((id, state) => {
    if (state) editStates.current[id] = state;
    else delete editStates.current[id];
  }, []);
  // bumped on every full load so rows rebuild from server data (e.g. after Reset all)
  const [listVersion, setListVersion] = useState(0);

  // countsOnly: refresh the counts but keep the rows on screen, so a record
  // just processed doesn't vanish from the Unprocessed list under the cursor
  const loadRecords = useCallback(
    async (countsOnly = false) => {
      try {
        const data = await getGeneratedRecords({ view, q: debouncedSearch, date, page, limit: perPage });
        setCounts(data.counts);
        if (countsOnly) return;
        setRecords(data.records);
        setListVersion((v) => v + 1);
        setPages(data.pages);
        setTotal(data.total);
        if (page > data.pages) setPage(data.pages);
      } catch (err) {
        setError(err.message);
      }
    },
    [view, debouncedSearch, date, page, perPage]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  // while the model retrains in the background, check again shortly
  useEffect(() => {
    if (!status?.retraining && !status?.measuringAccuracy) return undefined;
    const t = setTimeout(refresh, 2000);
    return () => clearTimeout(t);
  }, [status, refresh]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  // new filter -> back to page 1
  useEffect(() => {
    setPage(1);
  }, [view, debouncedSearch, date, perPage]);

  const createRecords = async (n) => {
    setCreating(n);
    setError(null);
    try {
      await createGeneratedRecords(n);
      // new records are unprocessed: show them
      if (view === "processed") setView("unprocessed");
      setPage(1);
      await loadRecords();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(0);
    }
  };

  // one Training Data.csv row by its Sr No -> unprocessed record
  const addBySr = async () => {
    const text = window.prompt("Sr No of the Training Data row to add (e.g. 92):");
    if (text === null) return;
    const sr = parseInt(text.trim(), 10);
    if (!(sr >= 1)) {
      window.alert("Enter a Sr No (1 or more).");
      return;
    }
    setCreating(1);
    setError(null);
    try {
      await addRecordBySrNo(sr);
      setView("unprocessed");
      setPage(1);
      await loadRecords();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(0);
    }
  };

  // Add record popup (every value typed by hand)
  const [showAdd, setShowAdd] = useState(false);
  const afterAdd = useCallback(async () => {
    setView("unprocessed");
    setPage(1);
    await loadRecords();
  }, [loadRecords]);

  // Upload CSV popup
  const [showUpload, setShowUpload] = useState(false);
  const afterUpload = useCallback(async () => {
    setView("processed");
    setPage(1);
    await loadRecords();
  }, [loadRecords]);

  const processAll = async () => {
    const rows = Object.values(editStates.current);
    const outside = rows.filter((r) => r.out);
    if (outside.length) {
      window.alert(
        `Values outside the range — fix them before processing:\n\n${outside
          .map((r) => `${r.name}: ${r.out}`)
          .join("\n")}`
      );
      return;
    }
    const edited = rows.filter((r) => r.edited).map((r) => r.name);
    const note = edited.length
      ? `\n\nUnsaved edits on ${edited.join(", ")} are NOT included — use Process on those records to keep them.`
      : "";
    if (!window.confirm(`Mark all ${counts.unprocessed} unprocessed records as processed, with their current results?${note}`)) return;
    setBulkBusy(true);
    setError(null);
    try {
      await processAllGeneratedRecords();
      await loadRecords();
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkBusy(false);
    }
  };

  const deleteAll = async () => {
    if (
      !window.confirm(
        `Delete ALL ${counts.all} records? This cannot be undone.\n\nThe verification history and the model stay as they are.`
      )
    )
      return;
    setBulkBusy(true);
    setError(null);
    try {
      await deleteAllGeneratedRecords();
      setPage(1);
      await loadRecords();
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkBusy(false);
    }
  };

  const resetAll = async () => {
    if (
      !window.confirm(
        "Reset ALL records to unprocessed? Edits are undone, results go back to the generated ones and Verify / Wrong prediction marks are cleared (the verification history is kept)."
      )
    )
      return;
    setBulkBusy(true);
    setError(null);
    try {
      await resetAllGeneratedRecords();
      await loadRecords();
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkBusy(false);
    }
  };

  // every record of the selected tab (with search / date), all pages, as CSV
  const [downloading, setDownloading] = useState(false);
  const downloadView = async () => {
    setDownloading(true);
    setError(null);
    try {
      const all = [];
      for (let p = 1, last = 1; p <= last; p += 1) {
        const data = await getGeneratedRecords({ view, q: debouncedSearch, date, page: p, limit: 100 });
        all.push(...data.records);
        last = data.pages;
      }
      const blob = new Blob([recordsCsv(all)], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${view}_records_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  // a fresh unprocessed copy with the same original values, shown on top
  const duplicateRecord = async (record) => {
    setError(null);
    try {
      await duplicateGeneratedRecord(record._id);
      setView("unprocessed");
      setPage(1);
      await loadRecords();
    } catch (err) {
      setError(err.message);
    }
  };

  const removeRecord = async (record) => {
    if (!window.confirm(`Delete record ${record.name}?`)) return;
    try {
      await deleteGeneratedRecord(record._id);
      await loadRecords();
    } catch (err) {
      setError(err.message);
    }
  };

  // records arrive newest batch first; keep that order, grouped by batch
  const batches = useMemo(() => {
    const out = [];
    records.forEach((r) => {
      const last = out[out.length - 1];
      if (last && last.name === r.batch) last.records.push(r);
      else out.push({ name: r.batch, records: [r] });
    });
    return out;
  }, [records]);

  const remove = async (id) => {
    if (!window.confirm("Delete this verification?")) return;
    try {
      await deletePredictionFeedback(id);
      await Promise.all([refresh(), loadRecords()]);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="rp-container">
      <Header />
      <div className="rp-body">
        <div className="rp-panel rp-table-panel">
          <div className="rp-table-toolbar">
            <div>
              <h3>Generated records</h3>
              <span className="mt-model-line">
                {status?.retraining ? (
                  <>
                    <FiRefreshCw className="mt-spin" /> Updating the model…
                  </>
                ) : status && !status.autoRetrain ? null : status?.trained ? (
                  `Model updated ${new Date(status.trained_at).toLocaleString()} · learned from ${status.feedback_rows} reviewed record${status.feedback_rows === 1 ? "" : "s"}`
                ) : (
                  "The model learns each time you Verify or mark a Wrong prediction"
                )}
              </span>
            </div>
          </div>
          <AccuracyLine status={status} />
          <div className="rp-table-controls mt-toolbar-buttons">
            <button
              className="mt-btn mt-btn--ranges"
              onClick={() => setShowRanges(true)}
              disabled={!status?.ranges}
              title="See the min / max for every input"
            >
              <FiSliders /> Show ranges
            </button>
            <label className="mt-inline">
              Records
              <input
                type="number"
                className="mt-count"
                min="1"
                max="10"
                value={newCount}
                onChange={(e) => setNewCount(e.target.value)}
              />
            </label>
            <button
              className="rp-download"
              onClick={() => createRecords(Number(newCount))}
              disabled={!!creating || !validCount}
              title={validCount ? "" : "Enter a number from 1 to 10"}
            >
              <FiPlus />
              {creating
                ? "Generating…"
                : `Generate ${validCount ? newCount : ""} new record${Number(newCount) === 1 ? "" : "s"}`}
            </button>
            <button
              className="mt-btn mt-btn--dark"
              onClick={addBySr}
              disabled={!!creating}
              title="Add one Training Data row, by its Sr No, as an unprocessed record"
            >
              <FiPlus /> Add by Sr No
            </button>
            <button
              className="mt-btn mt-btn--dark"
              onClick={() => setShowAdd(true)}
              disabled={!!creating}
              title="Type every input by hand and add it as one record"
            >
              <FiEdit3 /> Add record
            </button>
            <button
              className="mt-btn mt-btn--dark"
              onClick={() => setShowUpload(true)}
              title="CSV in the Training Data.csv format: each row is predicted and added as processed"
            >
              <FiUpload /> Upload Records
            </button>
          </div>
          <div className="mt-filters">
            <div className="mt-tabs" role="tablist">
              {["unprocessed", "processed", "verified", "all"].map((v) => (
                <button
                  key={v}
                  role="tab"
                  className={`mt-tab ${view === v ? "active" : ""}`}
                  onClick={() => setView(v)}
                >
                  {v === "all" ? "All" : STATUS_LABEL[v]}
                  <span className="mt-tab__count">{counts[v]}</span>
                </button>
              ))}
            </div>
            <input
              type="search"
              className="mt-search"
              placeholder="Search, e.g. 3-A or B-07-10-26"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <input
              type="date"
              className="mt-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              title="Show batches made on this day"
            />
            {date && (
              <button className="mt-btn mt-btn--icon" onClick={() => setDate("")} title="Clear date">
                <FiX />
              </button>
            )}
            <div className="mt-filters__bulk">
              <button
                className="mt-bulk-btn mt-bulk-btn--process"
                onClick={processAll}
                disabled={bulkBusy || !counts.unprocessed}
                title="Accept every unprocessed record with its current result"
              >
                <FiCheck /> Process all unprocessed
              </button>
              <button
                className="mt-bulk-btn mt-bulk-btn--reset"
                onClick={resetAll}
                disabled={bulkBusy || !counts.all}
                title="Put every record back to unprocessed"
              >
                <FiRotateCcw /> Reset all to unprocessed
              </button>
              <button
                className="mt-bulk-btn mt-bulk-btn--delete"
                onClick={deleteAll}
                disabled={bulkBusy || !counts.all}
                title="Delete every record (verification history and model are kept)"
              >
                <FiTrash2 /> Delete all records
              </button>
              <button
                className="mt-btn mt-btn--icon mt-btn--download"
                onClick={downloadView}
                disabled={downloading || !total}
                title={`Download ${view === "all" ? "all" : STATUS_LABEL[view].toLowerCase()} records (CSV)`}
                aria-label="Download CSV"
              >
                {downloading ? <FiRefreshCw className="mt-spin" /> : <FiDownload />}
              </button>
            </div>
          </div>

          {error && <p className="rp-error">{error}</p>}
          {!records.length && (
            <p className="rp-empty">
              {counts.all
                ? "No records match."
                : "No records yet. Choose how many (1–10) and click Generate to make random records (inside the training range)."}
            </p>
          )}
          {batches.map((b) => (
            <div key={b.name} className="mt-batch">
              <div className="mt-batch__head">
                Batch <b>{b.name}</b>
                <span className="rp-muted">
                  {b.records.length} record{b.records.length > 1 ? "s" : ""} ·{" "}
                  {b.records.filter((r) => r.verification?.is_correct != null).length} verified
                </span>
              </div>
              <div className="mt-list">
                {b.records.map((r) => (
                  <RecordRow
                    key={`${listVersion}-${r._id}`}
                    record={r}
                    ranges={status?.ranges}
                    onChanged={() => {
                      loadRecords(true);
                      refresh();
                    }}
                    onFeedback={refresh}
                    onDelete={removeRecord}
                    onDuplicate={duplicateRecord}
                    onEditState={onEditState}
                  />
                ))}
              </div>
            </div>
          ))}
          {total > 0 && (
            <div className="mt-pager">
              <span className="mt-pager__info">
                {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total} records
              </span>
              <div className="mt-pager__pages">
                <button className="mt-pager__btn" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                  <FiChevronLeft /> Previous
                </button>
                {pageNumbers(page, pages).map((n, i) =>
                  n === "…" ? (
                    <span key={`gap-${i}`} className="mt-pager__gap">…</span>
                  ) : (
                    <button
                      key={n}
                      className={`mt-pager__num ${n === page ? "active" : ""}`}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </button>
                  )
                )}
                <button className="mt-pager__btn" onClick={() => setPage(page + 1)} disabled={page >= pages}>
                  Next <FiChevronRight />
                </button>
              </div>
              <label className="mt-pager__size">
                Show
                <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
                  {[5, 10, 15, 20, 25, 30, 35, 40, 45, 50].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                per page
              </label>
            </div>
          )}
        </div>

        <div className="rp-panel rp-table-panel">
          <div className="rp-table-toolbar">
            <h3>
              Verification history <span className="rp-muted">({feedback.length})</span>
              {feedback.length > 0 && (
                <span className="mt-avg-match">
                  average accuracy{" "}
                  {Math.round(
                    feedback.reduce((sum, f) => sum + (f.match_pct ?? 0), 0) / feedback.length
                  )}
                  %
                </span>
              )}
            </h3>
          </div>
          <div className="rp-table-wrap">
            <table className="rp-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Location</th>
                  <th className="num">Predicted</th>
                  <th>Verdict</th>
                  <th className="num">Actual</th>
                  <th>Accuracy</th>
                  <th>Edited fields</th>
                  <th>Notes</th>
                  <th>By</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {feedback.map((f) => (
                  <tr key={f._id}>
                    <td>{new Date(f.createdAt).toLocaleString()}</td>
                    <td><b>{f.woreda}</b></td>
                    <td className="num">
                      {fmtPct(f.predicted_prevalence_pct)} <RiskBadge pct={f.predicted_prevalence_pct} />
                    </td>
                    <td>
                      <span className={`mt-verdict ${f.is_correct ? "ok" : "bad"}`}>
                        {f.is_correct ? "Correct" : "Wrong"}
                      </span>
                    </td>
                    <td className="num">{f.is_correct ? "—" : fmtPct(f.actual_prevalence_pct)}</td>
                    <td><MatchBadge value={f.match_pct} /></td>
                    <td className="mt-small">{(f.edited_fields || []).join(", ") || "—"}</td>
                    <td className="mt-small">{f.notes || "—"}</td>
                    <td className="mt-small">{f.verified_by || "—"}</td>
                    <td>
                      <button className="rp-modal__close" onClick={() => remove(f._id)} title="Delete">
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
                {!feedback.length && (
                  <tr>
                    <td colSpan={10} className="rp-empty">Nothing verified yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {showAdd && <AddRecordModal ranges={status?.ranges} onClose={() => setShowAdd(false)} onAdded={afterAdd} />}
      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUploaded={afterUpload} />}
      {showRanges && <RangesModal ranges={status?.ranges} onClose={() => setShowRanges(false)} />}
    </div>
  );
}
